/*
 * Presentation behaviour for the Luxury Fashion theme.
 *
 * Dawn owns commerce: the cart, the variant form, the sections API. Nothing here
 * touches any of that. These are two pieces of chrome that only exist because of
 * how this theme is laid out, and both degrade to plain, usable markup when the
 * script never runs.
 */
(() => {
  'use strict';

  const raf = (fn) => window.requestAnimationFrame(() => fn());

  /* ---------------------------------------------------------------------------
   * Swatch rows that scroll
   *
   * A card's colour row is a scroller so a product with twenty colours cannot
   * push the next row of the grid out of line. Arrows are added here rather than
   * in Liquid because whether a row overflows depends on the rendered width, not
   * on how many colours there are: the same eight swatches overflow on a phone
   * and do not on a desktop. A row that fits is left exactly as the server sent
   * it, with no controls to explain.
   * ------------------------------------------------------------------------- */
  function setUpSwatchRow(row) {
    const track = row.querySelector('[data-luxury-swatch-track]');
    if (!track || row.dataset.luxuryReady === 'true') return;

    const overflowing = () => track.scrollWidth - track.clientWidth > 2;
    if (!overflowing()) return;

    row.dataset.luxuryReady = 'true';

    const make = (direction, glyph) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `luxury-card__swatch-arrow luxury-card__swatch-arrow--${direction}`;
      button.setAttribute('aria-hidden', 'true');
      // Colour choice is reachable by opening the product; these only pan a
      // decorative preview, so they stay out of the tab order entirely.
      button.tabIndex = -1;
      button.textContent = glyph;
      return button;
    };

    const prev = make('prev', '‹');
    const next = make('next', '›');

    const sync = () => {
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    };

    const pan = (direction) => track.scrollBy({ left: direction * track.clientWidth * 0.8, behavior: 'smooth' });
    prev.addEventListener('click', (event) => { event.preventDefault(); pan(-1); });
    next.addEventListener('click', (event) => { event.preventDefault(); pan(1); });
    track.addEventListener('scroll', () => raf(sync), { passive: true });

    row.prepend(prev);
    row.append(next);
    sync();
  }

  function scanSwatchRows(root = document) {
    root.querySelectorAll('[data-luxury-swatches]').forEach(setUpSwatchRow);
  }

  /* ---------------------------------------------------------------------------
   * Sticky product bar
   *
   * Appears once the real add-to-cart button has scrolled away, and submits that
   * same form rather than carrying a copy of it. A duplicated variant form is
   * the kind of thing that works until a merchant adds a third option and the
   * two copies disagree about which variant is in the cart.
   * ------------------------------------------------------------------------- */
  function setUpStickyBar(bar) {
    const form = document.getElementById(bar.dataset.luxuryStickyForm);
    const anchor = document.getElementById(bar.dataset.luxuryStickyAnchor);
    if (!form || !anchor) return;

    const submit = bar.querySelector('[data-luxury-sticky-submit]');
    if (submit) {
      submit.addEventListener('click', () => {
        // Let the form's own validation and Dawn's cart handling run.
        form.querySelector('[type="submit"]')?.click();
      });
    }

    bar.querySelector('[data-luxury-sticky-options]')?.addEventListener('click', () => {
      anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });

    if (!('IntersectionObserver' in window)) return;
    new IntersectionObserver(
      ([entry]) => bar.classList.toggle('luxury-sticky-bar--visible', !entry.isIntersecting),
      { rootMargin: '0px 0px -120px 0px' }
    ).observe(anchor);
  }

  function init(root) {
    scanSwatchRows(root);
    (root.querySelectorAll ? root : document).querySelectorAll('[data-luxury-sticky]').forEach(setUpStickyBar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  // Filtering, pagination and the theme editor all replace markup in place, so
  // anything rendered after load has to be picked up again.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  window.addEventListener('pageshow', () => scanSwatchRows());

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => scanSwatchRows(), 200);
  });

  const grid = document.querySelector('#ProductGridContainer, .collection');
  if (grid && 'MutationObserver' in window) {
    new MutationObserver(() => raf(() => scanSwatchRows(grid))).observe(grid, { childList: true, subtree: true });
  }
})();
